import { useEffect, useState } from 'react';
import Button from '../components/Button';
import Input from '../components/Input';
import Select from '../components/Select';
import ConfirmDialog from '../components/ConfirmDialog';
import { smtpSettingsService } from '../services/adminApi';

export default function EmailSettings() {
  const [smtp, setSmtp] = useState({
    enabled: false,
    authMethod: 'Login',
    smtpUser: '',
    smtpPassword: '',
    pop3Server: '',
    smtpHost: '',
    smtpPort: 587,
    useTls: true,
    fromAddress: '',
  });
  const [policy, setPolicy] = useState({ restrictReuseDays: 9999, forceChangeDays: 9999 });
  const [testEmail, setTestEmail] = useState('');
  const [message, setMessage] = useState('');
  const [confirmAction, setConfirmAction] = useState<'smtp' | 'policy' | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    smtpSettingsService.get().then((res) => {
      if (res.data.smtpSettings) {
        const s = res.data.smtpSettings;
        setSmtp({
          enabled: s.enabled,
          authMethod: s.auth_method,
          smtpUser: s.smtp_user || '',
          smtpPassword: '',
          pop3Server: s.pop3_server || '',
          smtpHost: s.smtp_host || '',
          smtpPort: s.smtp_port || 587,
          useTls: s.use_tls,
          fromAddress: s.from_address || '',
        });
      }
    });
    smtpSettingsService.getPasswordPolicy().then((res) => {
      setPolicy({
        restrictReuseDays: res.data.passwordPolicy.restrict_reuse_days,
        forceChangeDays: res.data.passwordPolicy.force_change_days,
      });
    });
  }, []);

  const performUpdateSmtp = async () => {
    setSaving(true);
    try {
      await smtpSettingsService.update(smtp);
      setMessage('SMTP settings updated');
      setConfirmAction(null);
    } finally {
      setSaving(false);
    }
  };

  const handleTestMail = async () => {
    if (!testEmail) return;
    try {
      await smtpSettingsService.testMail(testEmail);
      setMessage(`Test mail sent to ${testEmail}`);
    } catch (error: any) {
      setMessage(error.response?.data?.message || 'Test mail failed to send');
    }
  };

  const performUpdatePolicy = async () => {
    setSaving(true);
    try {
      await smtpSettingsService.updatePasswordPolicy(policy);
      setMessage('Password policy updated');
      setConfirmAction(null);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 max-w-2xl">
      <h1 className="text-2xl font-bold mb-6">Email & 2FA Settings</h1>

      {message && <div className="mb-4 text-sm text-green-700 bg-green-50 border border-green-200 rounded px-4 py-2">{message}</div>}

      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h2 className="font-semibold mb-4">Custom Mail Server Settings</h2>
        <div className="space-y-4">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={smtp.enabled} onChange={(e) => setSmtp({ ...smtp, enabled: e.target.checked })} />
            Enable custom SMTP server
          </label>
          <Select
            label="Authentication Method"
            value={smtp.authMethod}
            onChange={(e) => setSmtp({ ...smtp, authMethod: e.target.value })}
            options={[
              { value: 'None', label: 'None' },
              { value: 'Login', label: 'Login' },
              { value: 'Plain', label: 'Plain' },
            ]}
          />
          <Input
            label="SMTP User Name (Sender Email)"
            value={smtp.smtpUser}
            onChange={(e) => setSmtp({ ...smtp, smtpUser: e.target.value })}
          />
          <Input
            label="SMTP Password"
            type="password"
            value={smtp.smtpPassword}
            onChange={(e) => setSmtp({ ...smtp, smtpPassword: e.target.value })}
          />
          <Input
            label="POP3 Server Name"
            value={smtp.pop3Server}
            onChange={(e) => setSmtp({ ...smtp, pop3Server: e.target.value })}
          />
          <Input
            label="Outgoing Mail Server (SMTP) Host"
            value={smtp.smtpHost}
            onChange={(e) => setSmtp({ ...smtp, smtpHost: e.target.value })}
          />
          <Input
            label="Port"
            type="number"
            value={smtp.smtpPort}
            onChange={(e) => setSmtp({ ...smtp, smtpPort: Number(e.target.value) })}
          />
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={smtp.useTls} onChange={(e) => setSmtp({ ...smtp, useTls: e.target.checked })} />
            Use TLS/SSL
          </label>
          <Input
            label="From Address"
            value={smtp.fromAddress}
            onChange={(e) => setSmtp({ ...smtp, fromAddress: e.target.value })}
          />
          <div className="flex gap-3">
            <Button onClick={() => setConfirmAction('smtp')}>Update</Button>
            <div className="flex gap-2 items-center">
              <Input
                placeholder="Send test mail to..."
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
              />
              <Button variant="secondary" onClick={handleTestMail}>
                Test Mail
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="font-semibold mb-4">Password Policy</h2>
        <div className="space-y-4">
          <Input
            label="Restrict login if password reused within (days, 9999 = never)"
            type="number"
            value={policy.restrictReuseDays}
            onChange={(e) => setPolicy({ ...policy, restrictReuseDays: Number(e.target.value) })}
          />
          <Input
            label="Force password change after (days, 9999 = never)"
            type="number"
            value={policy.forceChangeDays}
            onChange={(e) => setPolicy({ ...policy, forceChangeDays: Number(e.target.value) })}
          />
          <Button onClick={() => setConfirmAction('policy')}>Save Policy</Button>
        </div>
      </div>

      <ConfirmDialog
        isOpen={confirmAction === 'smtp'}
        title="Update SMTP Settings"
        message="Are you sure you want to update the mail server settings?"
        confirmLabel="Update"
        variant="primary"
        loading={saving}
        onConfirm={performUpdateSmtp}
        onCancel={() => setConfirmAction(null)}
      />

      <ConfirmDialog
        isOpen={confirmAction === 'policy'}
        title="Save Password Policy"
        message="Are you sure you want to save this password policy?"
        confirmLabel="Save"
        variant="primary"
        loading={saving}
        onConfirm={performUpdatePolicy}
        onCancel={() => setConfirmAction(null)}
      />
    </div>
  );
}
